import React, { useMemo } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import Status404NotFound from './Status404NotFound';
import FetcherObjectTable from './FetcherObjectTable';
import path from 'path';
import PageTitle from './Title';
import FormReport from './FormReport';
import dateTimeFormat from 'mrf/utils/DateTimeFormatter';
import moment from "moment";
import { Grid, Col } from './Grid';
import Button from './Button';
import PropTypes from 'prop-types';
import useCurrentUser from 'mrf/utils/useCurrentUser';
import usePrintMode from 'mrf/utils/usePrintMode';
import IF from './IF';
import T from 'mrf/utils/T';

Report.propTypes = {
    endpoint: PropTypes.string, // Query endpoint.
    context: PropTypes.object, // Data context that will be stored in redux
    history: PropTypes.object, // Browser history to handle back button
    title: PropTypes.string, // Report title
    group: PropTypes.string, // Report group to concatenate into api endpoint. It represents the backend folder name (eg. weapon, ammunition, etc)
    tableFields: PropTypes.array, // Table fields array
    onTableClick: PropTypes.func, // Callback Handle table clicks. It will pass two arguments: the row object and its index.
    children: PropTypes.object, // Form filter to filter the report.
    map: PropTypes.func, // Map form before sending to backend.
};

const ReportTable = ({ context, group, tableFields, onTableClick, title, includeSearch = true }) => {
    const currentDateTime = moment();
    const history = useHistory();
    const user = useCurrentUser();
    const printMode = usePrintMode();

    const handleBack = () => {
        history.goBack();
    }

    const userIdentificationText = user?.fullName
        ? `${user?.fullName} (${user?.userName})`
        : user?.userName;

    const sortableTableFields = useMemo(
        () => tableFields.map(f => ({ sortable: true, ...f })),
        [tableFields],
    );

    const groupURL = group ? group + '/' : '';

    return <>
        <PageTitle>{title}</PageTitle>
        <div>
            <FetcherObjectTable
                resource={context.resource}
                endpoint={`/api/reports/${groupURL}${context.resource}` + (includeSearch ? '/search' : '') + `${window.location.search}`}
                fields={sortableTableFields}
                onClick={onTableClick}
                movable={false}
                csvTitle={title}
                csvUser={userIdentificationText}
                readOnly={printMode}
            />
            {/* TODO: [CDI-438] Add user's Full Name at the report footer. */}
            <Grid className='mt-4'>
                <Col size={9}>
                    <p className="text-gray-400">
                        <T shouldUnescape={true}>Report generated at</T> { dateTimeFormat(currentDateTime) } <T>by</T> { userIdentificationText }.
                    </p>
                </Col>
                <Col size={3}>
                    <IF condition={!printMode}>
                        <Button color='secondary' type='button' onClick={handleBack}>Back</Button>
                    </IF>
                </Col>
            </Grid>
        </div>
    </>
};

export default function Report({
    match,
    context,
    history,
    title,
    group,
    tableFields,
    onTableClick,
    children,
    map,
    ...props
}) {
    const form = () => <>
        <FormReport
            match={match}
            title={title}
            context={context}
            history={history}
            map={map}
        >
            {children}
        </FormReport>
    </>;

    return <Switch>
        <Route exact path={match.url} render={form} />
        <Route path={path.join(match.url, 'search')}>
            <ReportTable
                context={context}
                group={group}
                tableFields={tableFields}
                onTableClick={onTableClick}
                title={title}
                {...props}
            />
        </Route>;
        <Route path='/' component={Status404NotFound} />
    </Switch>;
}
